import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import UIkit from "uikit";

import {
  badgeStatusMeta,
  getVerificationTooltip,
  getVerificationTooltipAttr,
  normalizeStatus,
} from "./identityVerificationStatus";
import IdentityVerificationModal from "./IdentityVerificationModal";
import {
  clearDecisionReconciliation,
  getDecisionReconciliationStatus,
  isDecisionReconciliationResolved,
  readDecisionReconciliation,
} from "./identityVerificationReconciliation";
import { ReactComponent as IdentityVerificationStatusIcon } from "../../assets/icons/status/idv.svg";

const IdentityVerificationBadge = ({
  reservation = {},
  status,
  enabled = false,
  className = "",
  variant = "badge",
  lookup,
}) => {
  const badgeRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [refreshedStatus, setRefreshedStatus] = useState(null);
  const lookupState = lookup?.state;
  const lookupResponse = lookup?.response;

  const reservationStatus = useMemo(() => {
    if (lookupState === "loaded") {
      return normalizeStatus(lookupResponse.verification_status
        || (lookupResponse.identity_verification_required ? "required" : "not_required"));
    }
    const raw =
      status ||
      reservation?.verification_status ||
      reservation?.identity_verification?.status ||
      (reservation?.identity_verification_required ? "required" : "not_required");

    return normalizeStatus(raw);
  }, [reservation, status, lookupState, lookupResponse]);

  useEffect(() => {
    if (lookupState && lookupState !== "loaded") return;
    const reconciliation = readDecisionReconciliation(
      reservation?.guest_reservation_id,
    );
    if (!reconciliation) {
      setRefreshedStatus(null);
      return;
    }
    if (isDecisionReconciliationResolved(reconciliation, reservationStatus)) {
      clearDecisionReconciliation(reservation?.guest_reservation_id);
      setRefreshedStatus(null);
      return;
    }
    setRefreshedStatus(getDecisionReconciliationStatus(reconciliation));
  }, [reservation?.guest_reservation_id, reservationStatus, lookupState]);

  const value = refreshedStatus || reservationStatus;

  const meta = badgeStatusMeta[value] || null;
  const tooltip = useMemo(() => getVerificationTooltip(value), [value]);
  const tooltipAttr = useMemo(() => getVerificationTooltipAttr(value), [value]);
  const toneClass =
    meta?.tone === "success"
      ? "identity-verification-tag--success"
      : meta?.tone === "warning"
        ? "identity-verification-tag--warning"
        : meta?.tone === "danger"
          ? "cancelled"
          : null;
  useEffect(() => {
    if (badgeRef.current && meta) {
      UIkit.update(badgeRef.current);
    }
  }, [tooltipAttr, meta]);

  const canReview = Boolean(
    meta &&
    reservation?.guest_reservation_id &&
    !["not_required", "required"].includes(value),
  );
  const handleModalClose = useCallback(() => setModalOpen(false), []);
  const handleStatusChange = useCallback((nextStatus) => {
    if (nextStatus) setRefreshedStatus(normalizeStatus(nextStatus));
  }, []);

  if (enabled !== true) return null;

  if (lookup && lookup.state !== "loaded" && !modalOpen) {
    return null;
  }

  // Reservations without an active IDV requirement intentionally have no pill.
  if (!meta) return null;

  const content = meta.label;
  const modal = canReview ? (
    <IdentityVerificationModal
      open={modalOpen}
      reservation={reservation}
      status={value}
      onClose={handleModalClose}
      onStatusChange={handleStatusChange}
    />
  ) : null;

  if (variant === "status") {
    const statusContent = (
      <>
        <IdentityVerificationStatusIcon
          className={`reservation-status-icon reservation-status-icon--${meta.tone}`}
          aria-hidden="true"
        />
        <span>{content}</span>
      </>
    );

    return (
      <>
        {canReview ? (
          <button
            ref={badgeRef}
            type="button"
            className={clsx(
              "reservation-status-row",
              "identity-verification-status-row--button",
              className,
            )}
            title={tooltip}
            uk-tooltip={tooltipAttr}
            onClick={() => {
              setRefreshedStatus(value);
              setModalOpen(true);
            }}
          >
            {statusContent}
          </button>
        ) : (
          <div
            ref={badgeRef}
            className={clsx("reservation-status-row", className)}
            title={tooltip}
            uk-tooltip={tooltipAttr}
          >
            {statusContent}
          </div>
        )}
        {modal}
      </>
    );
  }

  return (
    <>
      {canReview ? (
        <button
          ref={badgeRef}
          type="button"
          className={clsx(
            "hms-booking-tag",
            "identity-verification-tag--button",
            toneClass,
            className,
          )}
          title={tooltip}
          uk-tooltip={tooltipAttr}
          onClick={() => {
            setRefreshedStatus(value);
            setModalOpen(true);
          }}
        >
          {content}
        </button>
      ) : (
        <div
          ref={badgeRef}
          className={clsx("hms-booking-tag", toneClass, className)}
          title={tooltip}
          uk-tooltip={tooltipAttr}
        >
          {content}
        </div>
      )}
      {modal}
    </>
  );
};

export default IdentityVerificationBadge;
